import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Mail, MessageCircle, Lock } from "lucide-react";
import { WHATSAPP_LINK, WHATSAPP_DISPLAY, EMAIL_CONTACT } from "@/lib/api";
import { BannerSlideshow } from "@/components/BannerSlideshow";

const navItems = [
  { to: "/", label: "Início" },
  { to: "/sobre", label: "Sobre Mim" },
  { to: "/psicanalise", label: "Psicanálise" },
  { to: "/adolescentes", label: "Adolescentes" },
  { to: "/blog", label: "Artigos" },
  { to: "/contato", label: "Contato" },
];

const BANNER_URL = "https://customer-assets.emergentagent.com/job_escuta-acolhimento/artifacts/w5rq046a_banner.png";
void BANNER_URL;

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Fixed nav strip - always solid white for visibility */}
      <nav
        data-testid="site-header"
        className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#8BA88E]/15 py-3"
      >
        <div className="container-narrow flex items-center justify-between lg:justify-center gap-6 relative">
          {/* Mobile hamburger - left */}
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 -ml-2 rounded-full text-[#2C352D]"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>

          {/* Desktop centered menu */}
          <div className="hidden lg:flex items-center gap-9">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={`nav-link-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors ${
                    isActive ? "text-[#2C352D]" : "text-[#3a4a3b] hover:text-[#8BA88E]"
                  }`
                }
                end={it.to === "/"}
              >
                {it.label}
              </NavLink>
            ))}
          </div>

          {/* WhatsApp button - right */}
          <a
            data-testid="nav-whatsapp-btn"
            href={WHATSAPP_LINK("Olá Cristina, vim pelo site e gostaria de conversar.")}
            target="_blank"
            rel="noreferrer"
            className="lg:absolute lg:right-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8BA88E] text-white text-xs sm:text-sm font-medium hover:bg-[#769379] transition-all shadow-md"
          >
            <MessageCircle size={14} /> <span className="hidden sm:inline">Agendar</span>
          </a>
        </div>

        {open && (
          <div data-testid="nav-mobile-menu" className="lg:hidden bg-[#FAF9F6] border-t border-[#8BA88E]/15 mt-3">
            <div className="container-narrow py-6 flex flex-col gap-4">
              {navItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  data-testid={`nav-mobile-link-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `text-base py-2 ${isActive ? "text-[#2C352D] font-medium" : "text-[#5B685C]"}`
                  }
                  end={it.to === "/"}
                >
                  {it.label}
                </NavLink>
              ))}
              <a
                data-testid="nav-mobile-whatsapp-btn"
                href={WHATSAPP_LINK()}
                target="_blank"
                rel="noreferrer"
                className="btn-primary mt-2"
              >
                <MessageCircle size={16} /> Agendar conversa
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Spacer below fixed nav, then banner */}
      <div className="h-[45px]" aria-hidden />
      <Link to="/" aria-label="Cristina Moreira | Psicanálise Clínica" className="block w-full">
        <BannerSlideshow />
      </Link>
    </>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#F5F5ED] border-t border-[#8BA88E]/15">
      <div className="container-narrow py-16 grid md:grid-cols-3 gap-12">
        <div>
          <img
            src="https://customer-assets.emergentagent.com/job_escuta-acolhimento/artifacts/3h21539r_logo%20site.png"
            alt="Cristina Moreira | Psicanálise Clínica"
            className="h-32 md:h-36 w-auto object-contain mb-4"
          />
          <p className="text-[#5B685C] font-light leading-relaxed text-sm">
            Um espaço de escuta, acolhimento e elaboração — atendimento online para adultos e adolescentes.
          </p>
          <p className="mt-4 text-xs uppercase tracking-widest text-[#8BA88E] font-medium" data-testid="footer-cbpc">
            CBPC 2022-5725
          </p>
        </div>
        <div>
          <p className="text-eyebrow mb-4">Navegação</p>
          <ul className="space-y-2">
            {navItems.map((it) => (
              <li key={it.to}>
                <Link
                  to={it.to}
                  data-testid={`footer-link-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm text-[#5B685C] hover:text-[#2C352D] transition-colors"
                >
                  {it.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-eyebrow mb-4">Contato</p>
          <ul className="space-y-3 text-sm text-[#5B685C]">
            <li>
              <a
                data-testid="footer-whatsapp"
                href={WHATSAPP_LINK()}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 hover:text-[#2C352D] transition-colors"
              >
                <MessageCircle size={15} /> {WHATSAPP_DISPLAY}
              </a>
            </li>
            <li>
              <a
                data-testid="footer-email"
                href={`mailto:${EMAIL_CONTACT}`}
                className="inline-flex items-center gap-2 hover:text-[#2C352D] transition-colors break-all"
              >
                <Mail size={15} /> {EMAIL_CONTACT}
              </a>
            </li>
            <li>
              <a
                data-testid="footer-instagram"
                href="https://instagram.com/psicanalisecomcristina"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 hover:text-[#2C352D] transition-colors"
              >
                <Instagram size={15} /> @psicanalisecomcristina
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[#8BA88E]/15">
        <div className="container-narrow py-6 flex flex-col md:flex-row gap-3 md:gap-6 items-center justify-between">
          <p className="text-xs text-[#5B685C]">
            © {new Date().getFullYear()} Cristina Moreira — Psicanálise Clínica. Todos os direitos reservados.
          </p>
          <Link
            data-testid="footer-admin-link"
            to="/admin/login"
            className="inline-flex items-center gap-2 text-xs text-[#5B685C] hover:text-[#8BA88E] transition-colors px-3 py-1.5 rounded-full border border-[#8BA88E]/25 hover:border-[#8BA88E]/60"
          >
            <Lock size={12} /> Acessar painel administrativo
          </Link>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="App min-h-screen flex flex-col bg-[#FAF9F6]">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
